<template>
  <div v-clickOutside="close" class="inline-block relative active:cursor-pointer">
    <div ref="iconRef" class="px-0 py-0" @click="toggleModal">
      <img class="w-21 h-21" :class="{'rounded-5 bg-grey-500': isOutlinedIcon && isDarkMode}" :src="icon.src" :alt="icon.name">
    </div>

    <teleport to="body">
      <div
        v-if="isDropdownVisible"
        class="dropdown absolute w-312 bg-primary text-app-color rounded-4 shadow-dropdown z-50 py-15"
        :class="[{'dropdown--sub-project': project?.parentId }, direction, { 'dropdown--project-default-icon': !project }]"
        :style="position"
        @click.stop
      >
        <div
          class="flex items-center mx-15 mb-5 px-10 rounded-4"
          :class="isDarkMode ? 'bg-hover' : 'bg-grey-400'"
        >
          <i
            class="icon-search cursor-pointer opacity-50 hover:opacity-100 anim-opacity"
            :class="{'opacity-100': isFocused}"
            @click.stop="toggleInput"
          />
          <input
            ref="inputRef"
            v-model="searchValue"
            type="text"
            placeholder="search"
            class="w-full text-14 bg-transparent pr-10 pl-10 py-6 focus:outline-none placeholder-app-color opacity-50 focus:opacity-100 anim-opacity"
            @input="onInput"
            @focus="isFocused = true"
            @blur="isFocused = false"
            @click.stop
          >
        </div>
        <div class="flex text-center border-b border-grey-300 text-14">
          <p
            class="w-1/2 p-5 cursor-pointer anim-color-200 border-b-3 -mb-1"
            :class="[
              activeState === 'original' ? 'border-dark-600' : 'border-transparent',
              isDarkMode ? 'hover:bg-dark-500' : 'hover:bg-menu-hover'
            ]"

            @click="changeState('original')"
          >
            Original
          </p>
          <p
            class="w-1/2 p-5 cursor-pointer anim-color-200 border-b-3 -mb-1"
            :class="[
              activeState === 'outlined' ? 'border-dark-600' : 'border-transparent',
              isDarkMode ? 'hover:bg-dark-500' : 'hover:bg-menu-hover'
            ]"
            @click="changeState('outlined')"
          >
            Outlined
          </p>
        </div>
        <div v-if="computedIcons.length" id="icons" ref="iconsRef" class="wrapper grid grid-cols-7 gap-10 px-15 max-h-160 overflow-y-auto pt-16 no-horizontal-scroll-bar">
          <div v-for="(item, index) in computedIcons" :key="item">
            <Icon
              :chunk-size="chunkSize"
              :icon="item"
              :index="index"
              :is-dark-mode="isDarkMode"
              :has-right-tooltip-position="tooltipPosition(index)"
              class="z-5"
              @updateChunkSize="updateChunkSize"
              @click="setIcon(item)"
            />
          </div>
        </div>
        <div v-else class="px-15 pt-20 pb-5 text-15 ml-5">
          No results
        </div>
      </div>
    </teleport>
  </div>
</template>

<script>
import icons from '@/core/icons'
import 'vue3-virtual-scroller/dist/vue3-virtual-scroller.css'
import Icon from '@/components/projects/Icon'

import { defineComponent, ref, computed, onBeforeUpdate } from 'vue'
import { useStore } from 'vuex'

export default defineComponent({
  name: 'Icons',

  components: { Icon },

  props: {
    project: { type: Object },
    optionalTop: { type: Number, default: 0 },
    optionalCoeff: { type: Number, default: 0 },
    disableModalToggle: { type: Boolean, default: false }
  },

  emits: ['leave', 'setIcon'],

  setup (props, { emit }) {
    const { getters, state } = useStore()

    const isDropdownVisible = ref(false)
    const position = ref(null)
    const direction = ref('dropdown__top')
    const searchValue = ref('')
    const isFocused = ref(false)
    const inputRef = ref(null)
    const iconsRef = ref(null)
    const iconRef = ref(null)
    const indexRefs = ref([])
    const chunkSize = ref(70)

    // to reset the refs before each update
    onBeforeUpdate(() => {
      indexRefs.value = []
    })

    const isMobile = computed(() => getters.isMobile)
    const isDarkMode = computed(() => getters.isDarkMode)
    const defaultProjectIcon = computed(() => state.settings?.defaultProjectIcon)

    const activeState = ref('original')

    const icon = computed(() => {
      const icon = props.project?.icon && icons.icons.some(item => item === props.project.icon)
        ? props.project.icon
        : defaultProjectIcon.value || 'purple_circle'

      return {
        src: require(`@/assets/images/icons/${icon}.png`),
        name: icon
      }
    })

    const isOutlinedIcon = computed(() => icon.value.name.match('outlined'))

    const computedIcons = computed(() => {
      if (searchValue.value.toLowerCase().trim() !== '') {
        const filteredIcons = icons.icons.filter(item => item.toLowerCase().match(searchValue.value.toLowerCase()))
        return filteredIcons.slice(0, chunkSize.value)
      } else {
        return icons[activeState.value].slice(0, chunkSize.value)
      }
    })

    const indexArray = computed(() => (startPosition) => {
      var arr = startPosition === 0 ? [0] : []

      for (let i = startPosition; i < computedIcons.value.length; i = i + 7) {
        arr.push(i)
      }

      return arr
    })

    const tooltipPosition = computed(() => index => {
      const hasRightPosition = indexArray.value(-1)
      const hasLeftPosition = indexArray.value(0)

      if (hasRightPosition.includes(index)) return 'right-0'
      if (hasLeftPosition.includes(index)) return 'left-0'
      return 'left-1/2 transform -translate-x-1/2'
    })

    function close () {
      searchValue.value = ''
      isDropdownVisible.value = false
      chunkSize.value = 70
      activeState.value = 'original'
    }

    function toggleModal () {
      setTimeout(() => {
        if (!props.disableModalToggle) {
          isDropdownVisible.value = !isDropdownVisible.value
        } else {
          isDropdownVisible.value = true
        }

        getPosition()

        setTimeout(() => {
          if (isDropdownVisible.value) {
            inputRef.value.focus()
          }
        }, 100)
      }, 10)
    }

    function setIcon (value) {
      emit('setIcon', value)
      close()
    }

    function getPosition () {
      const coeff = props.project?.parentId ? isMobile.value ? 35 : 45 : 20
      const innerHeight = window.innerHeight
      const iconRect = iconRef.value.getBoundingClientRect()
      const containerHeight = 264
      const iconHeight = 35 // height of icon and height of arrow on icons container
      const offset = iconRect.top + iconHeight + containerHeight
      const modalHeight = 280 // height of icons container and height of arrow on it and gap = 5px

      direction.value = offset > innerHeight ? 'dropdown__bottom' : 'dropdown__top'
      const top = offset > innerHeight ? iconRect.top - (modalHeight + props.optionalTop) : iconRect.top + iconHeight

      const left = iconRect.left - (coeff + props.optionalCoeff)

      position.value = `left: ${left}px; top: ${top}px`
    }

    function toggleInput () {
      isFocused.value = !isFocused.value
      inputRef.value.focus()
    }

    function changeState (state) {
      activeState.value = state
      searchValue.value = ''
      chunkSize.value = 70
      iconsRef.value.scrollTo(0, 0)
    }

    function onInput () {
      activeState.value = 'original'
      chunkSize.value = 70
    }

    let timer = null

    function updateChunkSize (value) {
      clearTimeout(timer)

      timer = setTimeout(() => {
        chunkSize.value = value + 14
      }, 100)
    }

    return {
      // data
      activeState,
      isDropdownVisible,
      position,
      direction,
      searchValue,
      isFocused,
      tooltipPosition,
      chunkSize,
      // refs
      inputRef,
      iconRef,
      iconsRef,
      indexRefs,
      // computeds
      isDarkMode,
      icon,
      computedIcons,
      isMobile,
      defaultProjectIcon,
      isOutlinedIcon,
      // functions
      close,
      toggleModal,
      setIcon,
      toggleInput,
      changeState,
      onInput,
      updateChunkSize
    }
  }
})
</script>

<style scoped lang="scss">
.dropdown {
  left: -20px;

  &::after {
    content: " ";
    position: absolute;
    left: 15px;
    border-style: solid;
  }

  &--sub-project {
    left: -45px;

    &::after {
      left: 41px;
    }

    @media (max-width: 767px) {
      left: -35px;

      &::after {
        left: 30px;
      }
    }
  }

  &--project-default-icon {
    &::after {
      left: 15px;
    }

    @media (max-width: 767px) {
      &::after {
        left: 65px;
      }
    }
  }
}

.dropdown__top {
  &::after {
    bottom: 100%;
    border-width: 0 15px 11px 15px;
    border-color: transparent transparent var(--canvas) transparent;
  }
}
.dropdown__bottom {
  &::after {
    top: 100%;
    border-width: 11px 15px 0 15px;
    border-color: var(--canvas) transparent transparent transparent;
  }
}
</style>
